import React, { MutableRefObject, ReactElement, useContext, useRef, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import {
    DeepMap,
    FieldError,
    FieldValues,
    FormProvider,
    useForm,
    UseFormMethods
} from '../../Vendor/react-hook-form/dist/index.ie11'
import { CodeAddModify, LanguageErrorCodes } from '../../../i18n'
import { addCodeURL, updateCodeURL } from '../../NonReact/constants/ApiConstants'
import { HIDE } from '../../NonReact/constants/class-names'
import { EMPTY_STRING } from '../../NonReact/constants/Common'
import { RESTMethods } from '../../NonReact/constants/REST'
import CLanguageHandler from '../../NonReact/Languages/LanguageHandler'
import { IsVarEmpty, isVarNotEmpty } from '../../NonReact/utils/GlobalFunctions'
import { RESTAPIError } from '../Common/API'
import { ALL_VALUE } from '../Common/API/ItemsGetters'
import { StatusCodes } from '../../NonReact/common/API/REST/RESTAPIClient'
import { HeadingLevel } from '../Common/Components/Heading'
// Utilities imports.
import { IconName } from '../Common/Components/Icon'
import { IconSize } from '../../NonReact/constants/React/Icon/Constants'
// import {SelectItem} from '../Common/Components/Selects/Types'
import Tabs from '../Common/Components/Tabs'
import { Tab } from '../Common/Components/Tabs/Types'
import { ON_CHANGE } from '../Common/Constants/HookFormEventNames'
// Contexts imports.
import { LanguageContext, MessageContext, ReactState } from '../Common/Contexts'
import FooterActions from './Actions/FooterActions'
import { AddCodeModalID, CodeTypesStringValues, CodeTypesValues, StyleClassNames } from './Constants/Constants'
import { mapToServerState } from './Mappers/mapToServerState'
import { requestMapper } from './Mappers/requestMapper'
import { ApplicationCodeFormData, ApplicationCodes, ErrorWrapper, FilterValues } from './Types/Types'
import General from './Views/General'
import ScopeAssignment from './Views/ScopeAssignment'
import { ServerErrorOptions } from '../Common/Types/Types'
import { getValueForDefault, getValueIfNullOrUndefined } from '../../NonReact/utils/ValueManipulation'
import { EMPTY_VALUE } from '../Common/Constants/DefaultValues'
import { MessageContextType } from '../Common/Contexts/MessageContext'
import { ServerErrorCode } from '../../NonReact/common/Types/Types'

/**
 * Component properties type.
 */
type Props = {
    viewOnly?: boolean
    closeModal: () => void
    filters?: FilterValues
    selectedCode?: Nullable<ApplicationCodes>
    refreshAction: () => void
}

/**
 * Configuration tabs container component.
 *
 * @param closeModal Modal close callback.
 * @param seletetCode The selected code from the legacy code list page.
 * @param refresh A function to refresh the list page on submission of the form.
 * @param filters Object containing the filter values for the list page.
 */
const AddCodesTabs: React.FC<Props> = ({
    closeModal,
    selectedCode,
    filters,
    refreshAction,
    viewOnly
}: Props): ReactElement => {
    const language = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(LanguageErrorCodes.NAME)
    language.loadBundle(CodeAddModify.NAME)

    // Context that allows error messages to be displayed on the modal
    const { displayError }: MessageContextType = useContext(MessageContext)

    // React Ref to keep track of the modal being in view
    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const [isSubmitting, setIsSubmitting]: ReactState<boolean> = useState<boolean>(false)
    const [isValidationPopperOpen, setIsValidationPopperOpen]: ReactState<boolean> = useState<boolean>(false)

    // Active tab ID.
    const [activeTabID, setActiveTabID]: ReactState<number> = useState<number>(AddCodeModalID.GENERAL)

    // State to store any form errors
    const [serverError, setServerError]: ReactState<Definable<string | ServerErrorOptions[]>> = useState<
        string | ServerErrorOptions[]
    >()

    const getCodeType = (): string => {
        // If a code has been requested, then we will return this value.
        if (!IsVarEmpty(selectedCode)) {
            return `${
                selectedCode?.CodeType === CodeTypesStringValues.ACCOUNT_CODE
                    ? CodeTypesValues.ACCOUNT_CODE
                    : CodeTypesValues.AGENT_NA
            }`
        }

        // If a code has not been requested, but the filtered code is -1 then we will return a default value.
        if (filters?.codeType === `${ALL_VALUE}`) {
            return `${CodeTypesValues.ACCOUNT_CODE}`
        }

        // Will return the filtered value
        return getValueForDefault(filters?.codeType, `${CodeTypesValues.ACCOUNT_CODE}`)
    }

    // Sets the default values for the form within the Modal.
    const defaultValues: MutableRefObject<ApplicationCodeFormData> = useRef({
        general: {
            guid: IsVarEmpty(selectedCode) ? EMPTY_VALUE : selectedCode?.CodeGUID ?? EMPTY_STRING,
            code: getValueIfNullOrUndefined(selectedCode?.Code, EMPTY_VALUE),
            codeType: getCodeType(),
            description: getValueIfNullOrUndefined(selectedCode?.Description, EMPTY_VALUE),
            updateExisting: false
        },
        scopeAssignment: {
            cme: getValueForDefault(filters?.cmeID, EMPTY_STRING),
            telephonyServer: !IsVarEmpty(selectedCode)
                ? selectedCode?.TelSysID ?? ALL_VALUE
                : getValueForDefault(filters?.telSysID, ALL_VALUE),
            partition: !IsVarEmpty(selectedCode)
                ? selectedCode?.PartIDInTelSys ?? ALL_VALUE
                : getValueForDefault(filters?.partitionID, ALL_VALUE),
            filterRecursively: true
        }
    })
    // Form context.
    const methods: UseFormMethods = useForm({
        defaultValues: defaultValues.current,
        mode: ON_CHANGE,
        reValidateMode: ON_CHANGE,
        shouldUnregister: false
    })

    // eslint-disable-next-line
    const {
        handleSubmit,
        errors,
        formState: { isValid }
    }: UseFormMethods = methods

    /**
     * Tab activation method.
     * @param newActiveTabID ID of tab to activate.
     */
    const activateTab = (newActiveTabID: number): void => {
        setActiveTabID(newActiveTabID)
    }

    const determineValidationErrors = (formErrors: DeepMap<FieldValues, FieldError>): boolean[] => [
        !!formErrors?.general,
        !!formErrors?.details
    ]

    const validationErrors: boolean[] = determineValidationErrors(errors)

    // eslint-disable-next-line
    const submitCodesConfiguration = async (state: ApplicationCodeFormData): Promise<void> => {
        // Will fire if save action was successful - Zero errors
        const successfulSubmit = (): void => {
            // Close Modal and Refresh the list page.
            closeModal()
            refreshAction()
        }

        // Will fire if save action was not successful - One or more errors
        const failedSubmit = (error: RESTAPIError | ErrorWrapper): void => {
            if ((error as RESTAPIError).HTTPStatus) {
                const theError: RESTAPIError = error as RESTAPIError

                if (
                    (!IsVarEmpty(theError?.HTTPStatus) && theError.HTTPStatus === StatusCodes.OK) ||
                    theError.HTTPStatus === StatusCodes.OK_NO_CONTENT
                ) {
                    displayError(theError)
                    return successfulSubmit()
                }

                const errorCodeLanguage: Nullable<string> = (LanguageErrorCodes as unknown as Record<string, string>)[
                    `Errorcode${theError.errorCode}`
                ]

                const serverErrors: ServerErrorOptions[] = [
                    {
                        className: StyleClassNames.ERROR,
                        message: theError.errorMessage || errorCodeLanguage,
                        title: language.get(CodeAddModify.CodeAddModify_ErrorMessage_Server_Error),
                        headingLevel: HeadingLevel.TWO,
                        icon: { name: IconName.ERRORCIRCLE, size: IconSize.LARGE }
                    }
                ]

                setServerError(serverErrors)
            } else {
                const theError: ErrorWrapper = error as ErrorWrapper

                const serverErrors: ServerErrorOptions[] = [
                    {
                        className: StyleClassNames.SUCCESS,
                        message: theError.successes,
                        title: language.get(CodeAddModify.CodeAddModify_ErrorMessage_Successful),
                        headingLevel: HeadingLevel.TWO,
                        icon: { name: IconName.ERRORCIRCLE, size: IconSize.LARGE }
                    },
                    {
                        className: StyleClassNames.ERROR,
                        message: theError.error.message,
                        title: language.get(CodeAddModify.CodeAddModify_ErrorMessage_Server_Error),
                        headingLevel: HeadingLevel.TWO,
                        icon: { name: IconName.ERRORCIRCLE, size: IconSize.LARGE }
                    }
                ]

                setServerError(serverErrors)
            }

            setIsSubmitting(false)

            return undefined
        }

        if (
            state.scopeAssignment &&
            !IsVarEmpty(state.scopeAssignment?.telephonyServer) &&
            !IsVarEmpty(state.scopeAssignment.partition)
        ) {
            const {
                scopeAssignment: { telephonyServer, partition }
            }: ApplicationCodeFormData = state

            if (IsVarEmpty(selectedCode)) {
                // The second parameter determines if the telephonyServer and partitionID are included in the request payload
                const serverState: Nullable<ApplicationCodes> = mapToServerState(state, RESTMethods.POST)

                // Makes a request to the Codes api in the format of JSON
                requestMapper(
                    addCodeURL(`${telephonyServer}`, `${partition}`),
                    {
                        method: RESTMethods.POST,
                        body: serverState
                    },
                    {
                        errorMessageMapper: (
                            serverErrorCodes: ServerErrorCode[]
                        ): { serverError: string; successfulCodes?: string } => {
                            const isPluralisedExists: string =
                                serverErrorCodes.filter((item: ServerErrorCode): boolean =>
                                    isVarNotEmpty(item.ErrorCode)
                                ).length > 1
                                    ? 's'
                                    : ''
                            const isPluralisedSuccessful: string =
                                serverErrorCodes.filter((item: ServerErrorCode): boolean =>
                                    isVarNotEmpty(item.CodeGUID)
                                ).length > 1
                                    ? 's'
                                    : ''

                            const replacementsExistingCodes: string[] = [
                                `${isPluralisedExists}`,
                                `${isPluralisedExists}`,
                                serverErrorCodes
                                    .filter((item: ServerErrorCode): boolean => isVarNotEmpty(item.ErrorCode))
                                    .map((item: ServerErrorCode): string => item.Code)
                                    .join(',')
                            ]

                            const replacementsSuccessfulCodes: string[] = [
                                `${isPluralisedSuccessful}`,
                                serverErrorCodes
                                    .filter((item: ServerErrorCode): boolean => isVarNotEmpty(item.CodeGUID))
                                    .map((item: ServerErrorCode): string => item.Code)
                                    .join(', ')
                            ]

                            const errorObject: { serverError: string; successfulCodes?: string } = {
                                serverError: language.getInterpolated(
                                    `${LanguageErrorCodes.NAME}_Errorcode${
                                        serverErrorCodes.filter((item: ServerErrorCode): boolean =>
                                            isVarNotEmpty(item.ErrorCode)
                                        )[0].ErrorCode
                                    }`,
                                    ...replacementsExistingCodes
                                )
                            }

                            if (
                                serverErrorCodes.some((item: ServerErrorCode): boolean => isVarNotEmpty(item.CodeGUID))
                            ) {
                                errorObject.successfulCodes = language.getInterpolated(
                                    `${LanguageErrorCodes.NAME}_Errorcode${
                                        serverErrorCodes.filter((item: ServerErrorCode): boolean =>
                                            isVarNotEmpty(item.ErrorCode)
                                        )[0].ErrorCode
                                    }Success`,
                                    ...replacementsSuccessfulCodes
                                )
                            }

                            return errorObject
                        },
                        overrideStatusCode: StatusCodes.CONFLICT
                    }
                )
                    .then(successfulSubmit)
                    .catch(failedSubmit)
            } else {
                const serverState: Nullable<ApplicationCodes> = mapToServerState(state, RESTMethods.PUT)

                requestMapper(
                    updateCodeURL(
                        `${selectedCode?.TelSysID}`,
                        `${selectedCode?.PartIDInTelSys}`,
                        selectedCode?.Code,
                        `${selectedCode.CodeType}`
                    ),
                    {
                        method: RESTMethods.PUT,
                        body: serverState
                    }
                )
                    .then(successfulSubmit)
                    .catch(failedSubmit)
            }
        }
    }

    // eslint-disable-next-line
    const invalidFormSubmit = (errors: DeepMap<FieldValues, FieldError>): void => {
        if (!isValid) {
            setActiveTabID(
                determineValidationErrors(errors).findIndex((validationError: boolean): boolean => validationError)
            )
            setIsValidationPopperOpen(true)

            setTimeout((): void => {
                if (isMounted.current) {
                    setIsValidationPopperOpen(false)
                }
            }, 2500)
        }

        return undefined
    }

    // List of all tabs settings.
    const tabs: Nullable<Tab[]> = [
        {
            ID: AddCodeModalID.GENERAL,
            isDisplayed: true,
            tabsMenuItem: {
                title: language.get(CodeAddModify.GeneralTitle),
                iconName: IconName.PIN_NUM_OUTLINED,
                onClick: (): void => activateTab(AddCodeModalID.GENERAL)
            },
            content: <General viewOnly={viewOnly} isCodeSelected={!IsVarEmpty(selectedCode)} />,
            hasError: validationErrors[AddCodeModalID.GENERAL]
        },
        {
            ID: AddCodeModalID.SCOPE_ASSIGNMENT,
            isDisplayed: true,
            tabsMenuItem: {
                title: language.get(CodeAddModify.ScopeAssignmentTitleTitle),
                iconName: IconName.GPS_FIXED_OUTLINED,
                onClick: (): void => activateTab(AddCodeModalID.SCOPE_ASSIGNMENT)
            },
            content: <ScopeAssignment filters={filters} viewOnly={viewOnly} />,
            hasError: validationErrors[AddCodeModalID.SCOPE_ASSIGNMENT]
        }
    ]

    return (
        <div className='configuration-tabs'>
            <FormProvider {...methods}>
                <form
                    // eslint-disable-next-line  @typescript-eslint/no-misused-promises
                    onSubmit={handleSubmit(submitCodesConfiguration, invalidFormSubmit)}
                    className={isSubmitting ? HIDE : EMPTY_STRING}
                    noValidate
                >
                    {tabs && <Tabs tabs={tabs} activeTabID={activeTabID} />}
                    {!viewOnly && (
                        <FooterActions
                            serverError={serverError}
                            isValidationPopperOpen={isValidationPopperOpen}
                            closeModal={closeModal}
                        />
                    )}
                </form>
            </FormProvider>
        </div>
    )
}

export default AddCodesTabs
